<template>
    <b-container fluid>
        <b-row class="no-gutters pt-2">
            <b-col md="12">
                <div v-if="metadata && userIsSubscriber" class="select-wrapper flex-md-row flex-column pb-3">
                    <span>Payment of "Security Deposit" due: </span>
                    <form-select
                        @change="submitMetadata"
                        v-model="metadata.payment_schedule.other_deposits_rule"
                        :options="paymentSecurityOptions"
                        class="mb-0"
                    />
                </div>
                <div v-if="metadata && userIsSubscriber" class="select-wrapper flex-md-row flex-column pb-3">
                    <span>"Security Deposit" label: </span>
                    <form-select
                        @change="submitMetadata"
                        v-model="metadata.other_deposit_label"
                        class="mb-0"
                        :options="securityDepositOptions"
                    />
                </div>
                <grid
                    :isSearch="false"
                    ref="grid"
                    :rowData="rowData"
                    :columnDefs="depositColumnDefs"
                    :isAutoWidth="true"
                    class="metadata-grid mb-2"
                    :isAutoHeight="true"
                    rowSelection="single"
                    @selection-changed="handleRowSelect"
                    @on-row-data-updated="initialRowSelect"
                >
                    <responsive-button @click="openAddDepositModal" variant="primary" :icon="['fas', 'plus']"
                        >Add Deposit</responsive-button
                    >
                </grid>

                <grid
                    v-if="selectedRow && rowData.length"
                    :isSearch="false"
                    ref="property_grid"
                    :rowData="properties"
                    :isAutoWidth="true"
                    :columnDefs="propertiesColumnDefs"
                    :isAutoHeight="true"
                    class="pt-2"
                >
                    <responsive-dropdown class="filter-dropdown" :items="propertiesFilterOptions" variant="default">
                        <span v-html="filterIcon" />{{ filterTitle }}
                    </responsive-dropdown>
                    <responsive-button @click="bulkAssignToProperty(true)" variant="default" :icon="['fas', 'plus']"
                        >Add "{{ truncatedTitle }}" to All Properties</responsive-button
                    >
                    <responsive-button @click="bulkAssignToProperty(false)" variant="default" :icon="['fas', 'minus']"
                        >Remove "{{ truncatedTitle }}" from All Properties</responsive-button
                    >
                </grid>
            </b-col>
        </b-row>
        <add-edit-deposit-modal
            @hide="initialValues = null"
            :modalId="modalId"
            :initialData="initialValues"
            @submit="onAddEditRecord"
        />
    </b-container>
</template>

<script>
import api from '../../api';
import { formatResponseError } from '../../utils/api';
import Grid from '../generic/grid/grid.vue';
import { PROPERTY_MANAGER_SECURITY_DEPOSIT_ENDPOINT } from '../../endpoints';
import { mapGetters } from 'vuex';
import responsiveButton from '../generic/responsive-button/responsive-button.vue';
import responsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import formSelect from '../generic/form-select/form-select.vue';

import EditDeleteActionCellRenderer from './cell-renderers/edit-delete-action-cell-renderer.vue';
import addEditDepositModal from './modals/add-edit-deposit-modal.vue';
import propertyManagerMixin from './property-manager-mixin';

export default {
    components: {
        Grid,
        responsiveButton,
        responsiveDropdown,
        formSelect,
        EditDeleteActionCellRenderer,
        addEditDepositModal,
    },
    mixins: [propertyManagerMixin],
    data() {
        return {
            endpoint: PROPERTY_MANAGER_SECURITY_DEPOSIT_ENDPOINT,
            propertyName: 'Deposit',
            loading: false,
        };
    },
    computed: {
        ...mapGetters('propertyManager', ['metadata']),
        depositColumnDefs() {
            return [
                {
                    field: 'title',
                    headerName: 'Name',
                    sort: 'asc',
                    autoWidthExclude: true,
                    minWidth: 200,
                },
                {
                    field: 'pre_auth',
                    headerName: 'Pre Auth?',
                    cellRenderer: ({ data }) => (data.pre_auth ? 'Yes' : 'No'),
                },
                {
                    field: 'value',
                    headerName: 'Value',
                },
                {
                    field: '',
                    cellRenderer: 'EditDeleteActionCellRenderer',
                    maxWidth: 210,
                    minWidth: 210,
                    cellRendererParams: {
                        onEdit: (data) => {
                            this.openEditDepositModal(data);
                        },
                        onDelete: async (data) => {
                            try {
                                await api.delete(this.endpoint(data.id));
                                this.fetchRowData().then(() => {
                                    this.initialRowSelect();
                                });
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({ text: generic || 'Could not delete deposit' });
                            }
                        },
                        deleteMessage:
                            'Are you sure you want to delete this deposit and remove it from all properties?',
                    },
                },
            ];
        },
        paymentSecurityOptions() {
            return [
                {
                    text: 'at time of reservation',
                    value: 'R',
                },
                {
                    text: 'at payment of remaining balance',
                    value: 'B',
                },
            ];
        },
        securityDepositOptions() {
            return this.metadata?.other_deposit_label_choices.map((label, i) => ({
                text: label,
                value: i,
            }));
        },
    },
    mounted() {
        this.fetchRowData();
    },
    methods: {
        openAddDepositModal() {
            this.modalId = 'add-deposit-modal';
            this.$nextTick(() => {
                this.$bvModal.show(this.modalId);
            });
        },
        openEditDepositModal(data) {
            this.modalId = 'edit-deposit-modal-' + data.id;
            this.initialValues = data;
            this.$nextTick(() => {
                this.$bvModal.show(this.modalId);
            });
        },
        async submitMetadata() {
            if (!this.loading) {
                try {
                    this.loading = true;
                    this.$parent.$emit('handleBusy', true);
                    await this.$store.dispatch('propertyManager/submitMetadata', {
                        ...this.metadata.payment_schedule,
                        other_deposit_label: this.metadata.other_deposit_label,
                    });
                    this.successToast({
                        text: 'Data saved successfully',
                    });
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text: generic || 'Could not submit metadata',
                    });
                } finally {
                    this.loading = false;
                    this.$parent.$emit('handleBusy', false);
                }
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

:deep(.metadata-grid) {
    display: inline;

    .ag-row-hover,
    .ag-row-selected {
        background: rgba(33, 150, 243, 0.1);
        cursor: pointer;
    }
}

.select-wrapper {
    display: flex;
    align-items: baseline;
    gap: 5px;

    span {
        width: 250px;
    }
}

@include media-breakpoint-down(md) {
    .select-wrapper {
        fieldset {
            width: 100%;
        }
    }
}
</style>
